import * as Sentry from "@sentry/nextjs";

// Network failures inside the Supabase fetch surface as TypeError or
// AuthRetryableFetchError. Safari (and the iOS WebView) reports the
// message as the literal string "Load failed"; Chrome/Firefox use
// "Failed to fetch" or "NetworkError". Match all three plus the
// AbortController timeout shape so users never see "Load failed" + the
// raw Supabase URL — they see a single friendly recovery message.
const NETWORK_COPY =
  "Couldn't reach our servers. Check your connection and try again.";
const UNKNOWN_COPY =
  "Something went wrong. Please try again in a moment.";

type Surface =
  | "login"
  | "signup-resend"
  | "password-reset-request"
  | "password-update"
  | "oauth-google";

interface KnownCase {
  match: (lowercasedMessage: string) => boolean;
  display: string;
}

interface AuthErrorLike {
  name?: string;
  message?: string;
  status?: number;
}

/**
 * Map a Supabase auth error to user-facing copy. Network errors and
 * unknown errors get generic copy; unknowns are also reported to Sentry
 * (with a `surface` tag) so we can spot recurring backend issues without
 * leaking the Supabase URL to end users.
 *
 * The caller passes any surface-specific known cases (e.g. "Invalid login
 * credentials" → "Wrong email or password") so the helper stays free of
 * page-specific copy.
 */
export function classifyAuthError(
  error: AuthErrorLike,
  surface: Surface,
  knownCases: KnownCase[] = [],
): { display: string; reportedToSentry: boolean } {
  const msg = (error.message ?? "").toLowerCase();
  const isNetwork =
    error.name === "AuthRetryableFetchError" ||
    msg === "load failed" ||
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("timeout") ||
    msg.includes("aborted");

  if (isNetwork) {
    return { display: NETWORK_COPY, reportedToSentry: false };
  }

  for (const c of knownCases) {
    if (c.match(msg)) return { display: c.display, reportedToSentry: false };
  }

  Sentry.captureException(error, {
    tags: { surface, auth_unhandled: "true" },
  });
  return { display: UNKNOWN_COPY, reportedToSentry: true };
}
